import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Rocket, TrendingUp, Eye, MessageSquare, Users, Zap, Plus, ChevronRight, Loader2, Home as HomeIcon, Briefcase, ShieldCheck } from "lucide-react";
import { api, fileUrl, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isServiceRole } from "../lib/roles";
import MobileScreenHeader from "../components/MobileScreenHeader";

/* Boost Hub — /boost. User picks one of their listings to promote,
 * then routes to /feature-property/:id (existing paid feature flow). */

const BENEFITS = [
    { icon: Eye,          label: "More Views" },
    { icon: TrendingUp,   label: "Better Visibility" },
    { icon: MessageSquare,label: "More Enquiries" },
    { icon: Users,        label: "More Leads" },
    { icon: Zap,          label: "Faster Opportunities" },
];

export default function BoostHub() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [properties, setProperties] = useState([]);
    const [portfolio, setPortfolio]   = useState(null);

    const isSvc = user && isServiceRole(user.role);

    useEffect(() => {
        if (!user) {
            navigate("/login?next=/boost", { replace: true });
            return;
        }
        const load = async () => {
            setLoading(true);
            try {
                const [pRes, svcRes] = await Promise.all([
                    api.get("/me/properties").then((r) => r.data?.items || r.data || []).catch(() => []),
                    isSvc
                        ? api.get("/me/portfolio").then((r) => r.data).catch(() => null)
                        : Promise.resolve(null),
                ]);
                setProperties(pRes);
                setPortfolio(svcRes);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [user, isSvc, navigate]);

    if (!user) return null; // redirect in flight

    const hasProperties = properties.length > 0;
    const hasService = portfolio && (portfolio.id || portfolio.professional_id);

    return (
        <div className="min-h-screen bg-[#f6f7fb] pb-8" data-testid="boost-hub-page">
            <MobileScreenHeader title="Boost Your Listing" subtitle="Get more visibility & leads" testid="boost-header" />

            {/* Hero benefits card */}
            <div className="px-4 -mt-2">
                <div
                    className="text-on-image rounded-2xl p-4 shadow-md"
                    style={{ background: "linear-gradient(135deg, #001a3d 0%, #002657 100%)" }}
                    data-testid="boost-hero"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, #F59E0B, #DAA520)" }}>
                            <Rocket className="w-5 h-5" style={{ color: "#001a3d" }} />
                        </div>
                        <div className="min-w-0">
                            <div className="font-heading font-extrabold text-white text-[15px] leading-tight">Get More Visibility & Leads</div>
                            <div className="text-[10.5px] mt-0.5" style={{ color: "#F5E5A5" }}>Promote your Property or Service on GPM</div>
                        </div>
                    </div>
                    <div className="mt-3 grid grid-cols-5 gap-1">
                        {BENEFITS.map((b) => {
                            const Icon = b.icon;
                            return (
                                <div key={b.label} className="flex flex-col items-center gap-1 text-center">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.12)" }}>
                                        <Icon className="w-3.5 h-3.5" style={{ color: "#F5E5A5" }} />
                                    </div>
                                    <span className="text-[8.5px] text-white/85 leading-tight">{b.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-[#002657]/60">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your listings…
                </div>
            ) : (!hasProperties && !hasService) ? (
                /* Empty state */
                <div className="px-4 mt-6" data-testid="boost-empty">
                    <div className="rounded-2xl bg-white border border-[#002657]/10 p-6 text-center">
                        <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ background: "#FBF6E0" }}>
                            <Rocket className="w-6 h-6" style={{ color: "#8B6712" }} />
                        </div>
                        <div className="mt-3 font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>
                            You don&apos;t have an active listing to promote yet.
                        </div>
                        <div className="text-[11.5px] mt-1" style={{ color: "rgba(0,38,87,0.6)" }}>
                            Add a property or service, then come back to boost it.
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-2">
                            <Link
                                to="/add-property"
                                data-testid="boost-empty-add-property"
                                className="inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl text-[12px] font-extrabold shadow"
                                style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                            >
                                <HomeIcon className="w-3.5 h-3.5" /> List Your Property
                            </Link>
                            <Link
                                to={isSvc ? "/my-portfolio" : "/register?intent=service"}
                                data-testid="boost-empty-add-service"
                                className="text-on-image inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl text-[12px] font-extrabold shadow"
                                style={{ background: "linear-gradient(135deg, #001a3d, #002657)", color: "#FFF" }}
                            >
                                <Briefcase className="w-3.5 h-3.5" /> Add Your Service
                            </Link>
                        </div>
                    </div>
                </div>
            ) : (
                /* Listings picker */
                <>
                    {hasService && (
                        <div className="px-4 mt-5" data-testid="boost-service-block">
                            <div className="text-[10px] uppercase tracking-widest font-extrabold mb-2" style={{ color: "#8B6712" }}>Your Service Profile</div>
                            <Link
                                to={`/feature-property/service-${portfolio.id || portfolio.professional_id || user.id}`}
                                data-testid="boost-promote-service"
                                onClick={(e) => {
                                    // Service boost isn't a supported flow yet in FeatureProperty which expects a property id.
                                    // Route them to my-portfolio with a hint. This avoids a 404 and keeps promise honest.
                                    e.preventDefault();
                                    navigate("/my-portfolio?promote=1");
                                }}
                                className="flex items-center gap-3 rounded-2xl bg-white border border-[#002657]/10 p-3 shadow-sm active:scale-[0.99] transition-transform"
                            >
                                <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#EDE9FE" }}>
                                    <Briefcase className="w-5 h-5" style={{ color: "#7C3AED" }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading font-extrabold text-[13px] truncate" style={{ color: "#002657" }}>
                                        {portfolio.title || user.name || "My Service"}
                                    </div>
                                    <div className="text-[10.5px]" style={{ color: "rgba(0,38,87,0.6)" }}>
                                        {(user.role || "").replace(/_/g, " ")} · Tap to promote
                                    </div>
                                </div>
                                <ChevronRight className="w-4 h-4" style={{ color: "rgba(0,38,87,0.4)" }} />
                            </Link>
                        </div>
                    )}

                    {hasProperties && (
                        <div className="px-4 mt-5" data-testid="boost-property-block">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[10px] uppercase tracking-widest font-extrabold" style={{ color: "#8B6712" }}>
                                    Your Properties ({properties.length})
                                </div>
                                <Link
                                    to="/add-property"
                                    data-testid="boost-add-more-property"
                                    className="inline-flex items-center gap-0.5 text-[10.5px] font-bold"
                                    style={{ color: "#2563EB" }}
                                >
                                    <Plus className="w-3 h-3" /> Add
                                </Link>
                            </div>
                            <div className="space-y-2">
                                {properties.map((p) => (
                                    <Link
                                        key={p.id}
                                        to={`/feature-property/${p.id}`}
                                        data-testid={`boost-property-${p.id}`}
                                        className="flex items-center gap-3 rounded-2xl bg-white border border-[#002657]/10 p-2.5 shadow-sm active:scale-[0.99] transition-transform"
                                    >
                                        <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0" style={{ background: "rgba(0,38,87,0.05)" }}>
                                            {p.images?.[0] ? (
                                                <img src={fileUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center" style={{ color: "rgba(0,38,87,0.35)" }}>
                                                    <HomeIcon className="w-5 h-5" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-heading font-bold text-[12.5px] truncate" style={{ color: "#002657" }}>{p.title}</div>
                                            <div className="text-[10.5px] truncate" style={{ color: "rgba(0,38,87,0.6)" }}>
                                                {[p.locality, p.city].filter(Boolean).join(", ")}
                                            </div>
                                            <div className="mt-0.5 flex items-center gap-2 text-[10px]">
                                                <span className="font-extrabold" style={{ color: "#F59E0B" }}>{formatINR(p.price)}</span>
                                                {p.featured && (
                                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-extrabold uppercase tracking-widest text-[8px]" style={{ background: "#D1FAE5", color: "#059669" }}>
                                                        <ShieldCheck className="w-2.5 h-2.5" /> Boosted
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "rgba(0,38,87,0.4)" }} />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className="mt-6 px-4 text-center text-[9.5px]" style={{ color: "rgba(0,38,87,0.4)" }}>
                Promotions increase reach — actual leads depend on demand, pricing & timing.
            </div>
        </div>
    );
}
